import type { UiMetric, UiTable } from "../types";
import type { ActivityAnalytics, ActivityAnalyticsSlot } from "../api";

const IST = "Asia/Kolkata";

export function formatPct(value: number | null | undefined): string {
  return value == null ? "—" : `${Math.round(value * 100)}%`;
}

const rupees = (value: number) =>
  `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

/** "Sat 1 Nov, 3:15–3:45 PM" in IST. */
export function formatSlotWhen(startsAt: string | null, endsAt: string | null): string {
  if (!startsAt) return "—";
  const start = new Date(startsAt);
  const day = start.toLocaleDateString("en-IN", { timeZone: IST, weekday: "short", day: "numeric", month: "short" });
  const time = (d: Date) =>
    d.toLocaleTimeString("en-IN", { timeZone: IST, hour: "numeric", minute: "2-digit", hour12: true }).toUpperCase();
  return endsAt ? `${day}, ${time(start)} – ${time(new Date(endsAt))}` : `${day}, ${time(start)}`;
}

const slotRow = (s: ActivityAnalyticsSlot): string[] => [
  s.activity_name,
  formatSlotWhen(s.starts_at, s.ends_at),
  s.capacity ? `${s.sold} / ${s.capacity}` : String(s.sold),
  rupees(s.revenue),
  formatPct(s.fill_rate),
  `${s.checked_in} (${formatPct(s.check_in_rate)})`,
];

/** Cards and tables for the activity analysis panel, from the analytics API response. */
export function buildActivityAnalyticsView(data: ActivityAnalytics): {
  hasSales: boolean;
  metrics: UiMetric[];
  activityTable: UiTable;
  popularTable: UiTable;
  slotTable: UiTable;
} {
  const { summary } = data;
  const metrics: UiMetric[] = [
    { label: "Activity tickets", value: String(summary.tickets), delta: `${summary.activity_entries} activity entries` },
    { label: "Activity revenue", value: rupees(summary.revenue), tone: "success" },
    {
      label: "Bundles vs single",
      value: `${summary.bundle_tickets} / ${summary.individual_tickets}`,
      delta: `${formatPct(summary.bundle_share)} bought as a bundle`,
    },
    {
      label: "Checked in",
      value: `${summary.checked_in_entries} / ${summary.activity_entries}`,
      delta: formatPct(summary.check_in_rate),
      tone: "info",
    },
  ];

  const slotHeaders = ["Activity", "Slot", data.capacity_known ? "Sold / seats" : "Sold", "Revenue", "Fill", "Checked in"];

  return {
    hasSales: summary.tickets > 0,
    metrics,
    activityTable: {
      headers: ["Activity", data.capacity_known ? "Sold / seats" : "Sold", "Revenue", "Fill", "Checked in"],
      rows: data.by_activity.map((a) => [
        a.activity_name,
        a.capacity ? `${a.sold} / ${a.capacity}` : String(a.sold),
        rupees(a.revenue),
        formatPct(a.fill_rate),
        `${a.checked_in} (${formatPct(a.check_in_rate)})`,
      ]),
    },
    popularTable: { headers: slotHeaders, rows: data.popular_slots.map(slotRow) },
    slotTable: { headers: slotHeaders, rows: data.by_slot.map(slotRow) },
  };
}
